import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Button,
  Spinner,
  Modal,
  ModalBody,
  Badge,
  Popover,
  PopoverBody,
  Nav,
  NavItem,
  NavLink
} from "reactstrap";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faTrophy,
  faClock,
  faBell,
  faBellSlash,
  faSearch,
  faCalendarAlt,
  faCalendarWeek,
  faCheckCircle,
  faTimes,
  faMapMarkerAlt,
  faLayerGroup,
  faTrash,
  faCheckDouble,
  faAngleDown,
  faBars,
  faCircle
} from "@fortawesome/free-solid-svg-icons";
import footballService from "../../services/api/football-service"; // <--- Real import active
import classnames from 'classnames';

// --- HELPER: Smart Date Selector ---
const DateSelector = ({ currentDate, onDateChange }) => {
    const [mode, setMode] = useState('months'); // 'months' | 'years'
    const [tempYear, setTempYear] = useState(currentDate.getFullYear());
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const generateYears = () => {
        const current = new Date().getFullYear();
        const years = [];
        for (let i = current - 2; i <= current + 3; i++) years.push(i);
        return years;
    };

    const handleMonthClick = (monthIndex) => {
        const newDate = new Date(currentDate);
        newDate.setFullYear(tempYear);
        newDate.setMonth(monthIndex);
        onDateChange(newDate);
    };

    return (
        <div className="custom-date-picker">
            <div className="picker-header">
                <span className={`picker-btn ${mode === 'months' ? 'active' : ''}`} onClick={() => setMode('months')}>
                    {months[currentDate.getMonth()]}
                </span>
                <span className="mx-1">,</span>
                <span className={`picker-btn ${mode === 'years' ? 'active' : ''}`} onClick={() => setMode('years')}>
                    {tempYear}
                </span>
            </div>
            <div className="picker-grid">
                {mode === 'months' && months.map((m, i) => (
                    <div 
                        key={m} 
                        className={`picker-cell ${currentDate.getMonth() === i && tempYear === currentDate.getFullYear() ? 'selected' : ''}`}
                        onClick={() => handleMonthClick(i)}
                    >
                        {m}
                    </div>
                ))}
                {mode === 'years' && generateYears().map(y => (
                    <div 
                        key={y} 
                        className={`picker-cell ${tempYear === y ? 'selected' : ''}`}
                        onClick={() => { setTempYear(y); setMode('months'); }}
                    >
                        {y}
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- SUB-COMPONENT: Tournament Chip ---
const TournamentGroup = ({
  group,
  date,
  activeConfigs,
  toggling,
  handleToggle,
  setSelectedMatch,
  handleGroupBatchToggle,
  isMatchModalOpen,
  allMatches = [],
  isMobileView // Prop to trigger "Dot" view
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [targetEl, setTargetEl] = useState(null);
  const openTimeout = useRef(null);
  const closeTimeout = useRef(null);
  const [batchLoading, setBatchLoading] = useState(false);

  useEffect(() => {
    if (isMatchModalOpen) setIsOpen(false);
  }, [isMatchModalOpen]);

  // Disable hover on mobile to prevent accidental triggers
  const handleMouseEnter = () => {
    if (isMatchModalOpen || isMobileView) return;
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    openTimeout.current = setTimeout(() => setIsOpen(true), 300);
  };

  const handleMouseLeave = () => {
    if (isMatchModalOpen || isMobileView) return;
    if (openTimeout.current) clearTimeout(openTimeout.current);
    closeTimeout.current = setTimeout(() => setIsOpen(false), 200);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (isMatchModalOpen) return;
    setIsOpen(!isOpen);
  };

  const formatTime = (ts) => {
    return new Date(ts * 1000).toLocaleTimeString("en-US", {
      hour: "numeric", minute: "2-digit", hour12: true
    });
  };

  const tournamentMatches = useMemo(() => {
    return allMatches.filter(m => m.match.tournament.t_slug === group.info.t_slug);
  }, [allMatches, group.info.t_slug]);

  const upcomingTournamentMatches = useMemo(() => {
    return tournamentMatches.filter(m => m.match.status !== 'completed');
  }, [tournamentMatches]);

  const hasUpcoming = upcomingTournamentMatches.length > 0;
  const upcomingActiveCount = upcomingTournamentMatches.filter(m => activeConfigs[m.match.m_slug]).length;

  const showSubAllButton = hasUpcoming && upcomingActiveCount < upcomingTournamentMatches.length;
  const showUnsubAllButton = hasUpcoming && upcomingActiveCount > 0;

  const onBatchAction = async (e, targetStatus) => {
    e.stopPropagation();
    setBatchLoading(true);
    await handleGroupBatchToggle(group, targetStatus);
    setBatchLoading(false);
  };

  const matchesInGroup = group.matches;
  let chipClass = 'chip-upcoming';
  let dotColor = '#1967d2'; // Default blue

  const hasLive = matchesInGroup.some(m => m.match.status === 'started');
  const hasActive = matchesInGroup.some(m => activeConfigs[m.match.m_slug]);
  const isAllCompleted = matchesInGroup.every(m => m.match.status === 'completed');
  const wasActiveAndFinished = matchesInGroup.some(m => activeConfigs[m.match.m_slug] && m.match.status === 'completed');

  if (hasLive) { chipClass = 'chip-live'; dotColor = '#ea4335'; }
  else if (isAllCompleted && wasActiveAndFinished) { chipClass = 'chip-active-completed'; dotColor = '#1e8e3e'; }
  else if (hasActive) { chipClass = 'chip-active'; dotColor = '#34a853'; }
  else if (isAllCompleted) { chipClass = 'chip-completed'; dotColor = '#5f6368'; }

  return (
    <div className="event-group-wrapper" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div
        ref={setTargetEl}
        className={`event-chip ${chipClass} ${isMobileView ? 'mobile-dot-view' : ''}`}
        onClick={handleClick}
      >
        {isMobileView ? (
             /* Mobile View: Just a dot */
            <FontAwesomeIcon icon={faCircle} style={{color: dotColor, fontSize: '6px'}} />
        ) : (
            /* Desktop View: Full Chip */
            <>
                <span className="title-text">{group.info.name}</span>
                <span className="count-badge">{group.matches.length} M</span>
                {hasLive && <span className="live-dot"></span>}
            </>
        )}
      </div>

      {targetEl && !isMatchModalOpen && (
        <Popover
          placement="auto"
          isOpen={isOpen}
          target={targetEl}
          toggle={() => setIsOpen(!isOpen)}
          trigger="legacy"
          container="body"
          className="custom-popover-shadow super-priority-popover"
        >
          <PopoverBody className="match-popover-body" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div className={`popover-header-modern ${chipClass}-header`}>
              <div className="d-flex justify-content-between align-items-start">
                <div style={{ flex: 1 }}>
                  <div className="popover-tournament-name">{group.info.name}</div>
                  <div className="popover-date-badge">
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                    {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                </div>

                {!isAllCompleted && (
                  <div className="d-flex flex-column gap-1 ms-2">
                    {batchLoading ? <Spinner size="sm" color="primary" /> : (
                      <>
                        {showSubAllButton && (
                          <Button size="sm" color="primary" className="py-0 px-2" style={{ fontSize: '9px', height: '20px', whiteSpace: 'nowrap' }} onClick={(e) => onBatchAction(e, true)}>
                            <FontAwesomeIcon icon={faCheckDouble} className="me-1" /> Sub All
                          </Button>
                        )}
                        {showUnsubAllButton && (
                          <Button size="sm" color="danger" className="py-0 px-2" style={{ fontSize: '9px', height: '20px', whiteSpace: 'nowrap' }} onClick={(e) => onBatchAction(e, false)}>
                            <FontAwesomeIcon icon={faTimes} className="me-1" /> Unsub All
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="popover-matches-list">
              {group.matches.map(m => {
                const isCompleted = m.match.status === 'completed';
                const isLiveNow = m.match.status === 'started';
                const isLiveBlogActive = activeConfigs[m.match.m_slug];

                let rowClass = "";
                if (isLiveNow) rowClass = "row-is-live";
                else if (isCompleted && isLiveBlogActive) rowClass = "row-was-active";
                else if (isLiveBlogActive) rowClass = "row-is-active";
                else if (isCompleted) rowClass = "row-is-completed";

                return (
                  <div key={m.match.m_slug} className={`popover-match-row ${rowClass}`}>
                    <div className="match-time-col">
                      {isCompleted ? <span className="status-finished">Ended</span> :
                        isLiveNow ? <span className="status-live">LIVE</span> :
                          <span className="status-time">{formatTime(m.match.start.timestamp)}</span>}
                    </div>
                    <div className="match-details-col" onClick={() => { setSelectedMatch(m); setIsOpen(false); }}>
                      <div className="match-teams" title={m.match.title}>
                        {m.teams?.[0]?.code || 'Home'} <span className="vs">vs</span> {m.teams?.[1]?.code || 'Away'}
                      </div>
                      <div className="match-subtext">{m.match.stadium?.city || 'Venue TBD'}</div>
                    </div>
                    <div className="match-action-col">
                      {isCompleted ? (
                        <FontAwesomeIcon icon={faCheckCircle} className={isLiveBlogActive ? "text-success" : "text-muted"} title="Completed" />
                      ) : (
                        <Button size="sm" color="link" className="action-btn"
                          disabled={toggling[m.match.m_slug]}
                          onClick={(e) => handleToggle(m, e)}
                          title={isLiveBlogActive ? "Unsubscribe" : "Subscribe"}>
                          {toggling[m.match.m_slug] ? <Spinner size="sm" color="primary" /> : (
                            <FontAwesomeIcon icon={isLiveBlogActive ? faBell : faBellSlash} className={isLiveBlogActive ? "icon-active" : "icon-inactive"} size="lg" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </PopoverBody>
        </Popover>
      )}
    </div>
  );
};


// --- MAIN COMPONENT ---
const LiveBlogManager = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [matches, setMatches] = useState([]);
  const [activeConfigs, setActiveConfigs] = useState({});
  const [loading, setLoading] = useState(false);

  const [allUpcomingMatches, setAllUpcomingMatches] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [upcomingPopoverOpen, setUpcomingPopoverOpen] = useState(false);
    
  const [upcomingPopoverTab, setUpcomingPopoverTab] = useState('matches');

  const popoverCloseTimer = useRef(null);
  const [selectedUpcomingSlugs, setSelectedUpcomingSlugs] = useState(new Set());

  const [showUpcomingOnly, setShowUpcomingOnly] = useState(false);

  const [subscribedRegistry, setSubscribedRegistry] = useState(new Map());

  const [subPopoverOpen, setSubPopoverOpen] = useState(false);
  const subPopoverTimer = useRef(null);

  const [findingDate, setFindingDate] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [toggling, setToggling] = useState({});

  const [selectedMatch, setSelectedMatch] = useState(null);
  const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);

  // New States
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const scrollCooldownRef = useRef(false); // Ref for scroll cooldown

  // Listener for mobile view resizing
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOpenMatchModal = (match) => {
    setSelectedMatch(match);
    setIsMatchModalOpen(true);
    setSubPopoverOpen(false);
    setUpcomingPopoverOpen(false);
    setMorePopoverState(prev => ({ ...prev, isOpen: false }));
  };

  const handleCloseMatchModal = () => {
    setSelectedMatch(null);
    setIsMatchModalOpen(false);
  };

  const [morePopoverState, setMorePopoverState] = useState({
    isOpen: false, targetId: null, items: [], dateLabel: null, fullDate: null
  });

  const [showSearch, setShowSearch] = useState(false);
  const [filters, setFilters] = useState({ query: "", tslug: "", roundSlug: "" });
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [allTournaments, setAllTournaments] = useState([]);
  const [allRounds, setAllRounds] = useState([]);
  const [roundOptions, setRoundOptions] = useState([]);

  const calendarRef = useRef(null);

  const upcomingTournamentsList = useMemo(() => {
    const map = {};
    allUpcomingMatches.forEach(m => {
        const tSlug = m.match.tournament.t_slug;
        if(!map[tSlug]) {
            map[tSlug] = {
                info: m.match.tournament,
                matches: [],
                tSlug: tSlug
            };
        }
        map[tSlug].matches.push(m);
    });
    return Object.values(map).sort((a,b) => a.info.name.localeCompare(b.info.name));
  }, [allUpcomingMatches]);

  const getActiveSubscribedCount = () => {
    let allSubbed = Array.from(subscribedRegistry.values());
    if (filters.tslug) {
      allSubbed = allSubbed.filter(m => m.match.tournament.t_slug === filters.tslug);
    }
    return allSubbed.filter(m => m.match.status !== 'completed').length;
  };

  const getCompletedSubscribedCount = () => {
    let allSubbed = Array.from(subscribedRegistry.values());
    if (filters.tslug) {
      allSubbed = allSubbed.filter(m => m.match.tournament.t_slug === filters.tslug);
    }
    return allSubbed.filter(m => m.match.status === 'completed').length;
  };

  const getUniqueUpcomingTournamentsCount = () => {
    if(!allUpcomingMatches.length) return 0;
    const uniqueSlugs = new Set(allUpcomingMatches.map(m => m.match.tournament.t_slug));
    return uniqueSlugs.size;
  }

  const handleSubscribedEnter = () => {
    if (isMatchModalOpen) return;
    if (subPopoverTimer.current) clearTimeout(subPopoverTimer.current);
    setSubPopoverOpen(true);
  };

  const handleSubscribedLeave = () => {
    subPopoverTimer.current = setTimeout(() => {
      setSubPopoverOpen(false);
    }, 300);
  };

  const getSubscribedMatchList = () => {
    let allSubbed = Array.from(subscribedRegistry.values());
    if (filters.tslug) {
      allSubbed = allSubbed.filter(m => m.match.tournament.t_slug === filters.tslug);
    }
    const active = allSubbed.filter(m => m.match.status !== 'completed');
    const finished = allSubbed.filter(m => m.match.status === 'completed');
    active.sort((a, b) => a.match.start.timestamp - b.match.start.timestamp);
    finished.sort((a, b) => b.match.start.timestamp - a.match.start.timestamp);
    return [...active, ...finished];
  };

  // --- FIXED SCROLL: Cooldown Logic ---
  const handleWheelScroll = (e) => {
    if (e.target.closest('.popover') || e.target.closest('.modal') || e.target.closest('.popover-matches-list')) return;
    
    // Prevent scrolling if on cooldown
    if (scrollCooldownRef.current) return;

    if (morePopoverState.isOpen) setMorePopoverState(prev => ({ ...prev, isOpen: false }));
    
    if (e.deltaY > 0) navigateDate(1);
    else navigateDate(-1);

    // Activate Cooldown
    scrollCooldownRef.current = true;
    setTimeout(() => {
        scrollCooldownRef.current = false;
    }, 800); 
  };

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [tournamentsData, roundsData] = await Promise.all([
          footballService.getTournaments({ size: 1000 }),
          footballService.getAllRounds()
        ]);
        if (tournamentsData.status === 'success') setAllTournaments(tournamentsData.data);
        if (roundsData.data) { setAllRounds(roundsData.data); setRoundOptions(roundsData.data); }
      } catch (err) { console.error(err); }
    };
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (!filters.tslug) { setRoundOptions(allRounds); return; }
    const selectedTournament = allTournaments.find(t => t.t_slug === filters.tslug);
    if (selectedTournament && selectedTournament.rounds) {
      const roundSlugsForTournament = selectedTournament.rounds.map(r => r.r_slug);
      setRoundOptions(allRounds.filter(r => roundSlugsForTournament.includes(r.r_slug)));
    } else { setRoundOptions([]); }
  }, [filters.tslug, allTournaments, allRounds]);

  useEffect(() => {
    const fetchGlobalTournamentData = async () => {
      setFindingDate(true);
      setLoadingUpcoming(true);

      try {
        const now = new Date();
        const startOfRange = new Date();
        const endOfRange = new Date(now.getFullYear() + 1, 11, 31); 

        const params = {
          fromDate: Math.floor(startOfRange.getTime() / 1000),
          toDate: Math.floor(endOfRange.getTime() / 1000),
          tslug: filters.tslug,
          roundSlug: filters.roundSlug,
          q: debouncedQuery,
          size: 2000 
        };

        const response = await footballService.getMatchesForCalendar(params);
        let foundMatches = response.data || [];
        foundMatches.sort((a, b) => a.match.start.timestamp - b.match.start.timestamp);

        const strictlyUpcoming = foundMatches.filter(m => m.match.status !== 'completed');
        setAllUpcomingMatches(strictlyUpcoming);
        const allSlugs = new Set(strictlyUpcoming.map(m => m.match.m_slug));
        setSelectedUpcomingSlugs(allSlugs);

        if (foundMatches.length > 0 && (filters.tslug || debouncedQuery)) {
          const nowTs = Math.floor(Date.now() / 1000);
          const upcomingMatch = foundMatches.find(m => m.match.start.timestamp >= nowTs);
          if (upcomingMatch) {
            const newDate = new Date(upcomingMatch.match.start.timestamp * 1000);
            if (newDate.getMonth() !== currentDate.getMonth() || newDate.getFullYear() !== currentDate.getFullYear()) {
              setCurrentDate(newDate);
            }
          }
        }
      } catch (e) {
        console.error("Error fetching global data:", e);
      } finally {
        setFindingDate(false);
        setLoadingUpcoming(false);
      }
    };

    const timer = setTimeout(() => { fetchGlobalTournamentData(); }, 100);
    return () => clearTimeout(timer);
  }, [filters.tslug, filters.roundSlug, debouncedQuery]);

  useEffect(() => {
    setSubscribedRegistry(prev => {
      const newMap = new Map(prev);
      matches.forEach(m => {
        if (activeConfigs[m.match.m_slug]) newMap.set(m.match.m_slug, m);
        else if (newMap.has(m.match.m_slug) && activeConfigs[m.match.m_slug] === false) newMap.delete(m.match.m_slug);
      });
      allUpcomingMatches.forEach(m => {
        if (activeConfigs[m.match.m_slug]) newMap.set(m.match.m_slug, m);
        else if (newMap.has(m.match.m_slug) && activeConfigs[m.match.m_slug] === false) newMap.delete(m.match.m_slug);
      });
      return newMap;
    });
  }, [matches, allUpcomingMatches, activeConfigs]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(filters.query), 500);
    return () => clearTimeout(timer);
  }, [filters.query]);

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    let fromDate, toDate;
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (view === "month") {
      const startOfMonth = new Date(year, month, 1);
      const endOfMonth = new Date(year, month + 1, 0);
      fromDate = Math.floor(startOfMonth.getTime() / 1000);
      toDate = Math.floor(endOfMonth.getTime() / 1000) + 86400;
    } else {
      const dayOfWeek = currentDate.getDay();
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - dayOfWeek);
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      fromDate = Math.floor(startOfWeek.getTime() / 1000);
      toDate = Math.floor(endOfWeek.getTime() / 1000);
    }

    const params = {
      fromDate, toDate, size: 500,
      q: debouncedQuery.trim(),
      tslug: filters.tslug,
      roundSlug: filters.roundSlug
    };

    try {
      const matchesData = await footballService.getMatchesForCalendar(params);
      const matchList = matchesData.data || [];

      let configs = {};
      const activeMatches = matchList.filter(m => m.match.status !== 'completed');
      if (activeMatches.length > 0) {
        const slugs = activeMatches.map((m) => m.match.m_slug).join(",");
        try {
          const configResponse = await footballService.getMatchConfigs(slugs);
          configs = configResponse.data || {};
        } catch (e) { console.warn(e); }
      }

      setMatches(matchList);
      setActiveConfigs(prev => ({ ...prev, ...configs }));
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch matches.");
    } finally {
      setLoading(false);
    }
  }, [currentDate, view, debouncedQuery, filters.tslug, filters.roundSlug]);

  useEffect(() => {
    const handler = setTimeout(() => { fetchMatches(); }, 500);
    return () => clearTimeout(handler);
  }, [fetchMatches]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      if (field === 'tslug') newFilters.roundSlug = "";
      return newFilters;
    });
  };

  const handleFilterReset = () => {
    setFilters({ query: "", tslug: "", roundSlug: "" });
    setShowUpcomingOnly(false);
    setShowSearch(false);
    setCurrentDate(new Date());
    setAllUpcomingMatches([]);
  };

  const handleBatchToggle = async () => {
    if (!selectedUpcomingSlugs.size) return;
    setBulkLoading(true);

    const matchesToToggle = allUpcomingMatches.filter(m => selectedUpcomingSlugs.has(m.match.m_slug));
    const areAllActive = matchesToToggle.every(m => activeConfigs[m.match.m_slug]);
    const targetStatus = !areAllActive;

    const payloadMatches = matchesToToggle.map(m => ({
      slug: m.match.m_slug,
      key: m.match.key,
      tslug: m.match.tournament.t_slug
    }));

    try {
      await footballService.toggleLiveBlogBatch({ matches: payloadMatches, status: targetStatus });
      const newConfigs = { ...activeConfigs };
      matchesToToggle.forEach(m => { newConfigs[m.match.m_slug] = targetStatus; });
      setActiveConfigs(newConfigs);
      toast.success(targetStatus ? "Matches subscribed successfully" : "Matches unsubscribed successfully");
      setUpcomingPopoverOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Bulk action failed.");
    } finally {
      setBulkLoading(false);
    }
  };

const handleGroupBatchToggle = async (group, targetStatus) => {
    let tSlug = group.info.t_slug;
    let label = group.info.name || "Group";
    let matchesToTarget = [];

    // FIX: Look into the global upcoming matches list, not just the currently visible ones
    if (tSlug) {
        matchesToTarget = allUpcomingMatches.filter(m => 
            m.match.tournament.t_slug === tSlug && 
            m.match.status !== 'completed'
        );
        
        // If for some reason the current month has matches not in allUpcomingMatches 
        // (e.g. they started today), merge them in to be safe
        const visibleMatches = matches.filter(m => 
            m.match.tournament.t_slug === tSlug && 
            m.match.status !== 'completed'
        );
        
        // Use a Map to ensure unique slugs
        const uniqueMatches = new Map();
        [...matchesToTarget, ...visibleMatches].forEach(m => uniqueMatches.set(m.match.m_slug, m));
        matchesToTarget = Array.from(uniqueMatches.values());

    } else if (filters.tslug && allUpcomingMatches.length > 0) {
        matchesToTarget = allUpcomingMatches;
        label = "All Upcoming";
    }

    if (matchesToTarget.length === 0) {
        toast.warn("No upcoming matches found for this tournament across the schedule.");
        return;
    }

    const payloadMatches = matchesToTarget.map(m => ({
      slug: m.match.m_slug,
      key: m.match.key,
      tslug: m.match.tournament.t_slug
    }));

    try {
      setBulkLoading(true); // Optional: add a loading state if you want
      await footballService.toggleLiveBlogBatch({ matches: payloadMatches, status: targetStatus });
      
      const newConfigs = { ...activeConfigs };
      matchesToTarget.forEach(m => { newConfigs[m.match.m_slug] = targetStatus; });
      setActiveConfigs(newConfigs);
      
      toast.success(`${label}: ${targetStatus ? "All future matches subscribed" : "All future matches unsubscribed"}`);
    } catch (err) {
      console.error(err);
      toast.error("Group action failed.");
    } finally {
      setBulkLoading(false);
    }
};

  const handleToggle = async (matchItem, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const match = matchItem.match;
    const slug = match.m_slug;
    const newStatus = !activeConfigs[slug];

    setToggling(prev => ({ ...prev, [slug]: true }));
    setActiveConfigs(prev => ({ ...prev, [slug]: newStatus }));

    try {
      await footballService.toggleLiveBlog({
        match_slug: slug, match_key: match.key, tournament_slug: match.tournament.t_slug, status: newStatus,
      });
      setSubscribedRegistry(prev => {
        const newMap = new Map(prev);
        if (newStatus) newMap.set(slug, matchItem);
        else newMap.delete(slug);
        return newMap;
      });
      // UPDATED MESSAGE
      toast.success(newStatus ? "Match subscribed successfully" : "Match unsubscribed successfully");
    } catch (err) {
      setActiveConfigs(prev => ({ ...prev, [slug]: !newStatus }));
      toast.error("Failed to update status.");
    } finally {
      setToggling(prev => ({ ...prev, [slug]: false }));
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (view === "month") newDate.setMonth(currentDate.getMonth() + direction);
    else newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };
  const goToToday = () => { setCurrentDate(new Date()); };
  const handleShowMoreClick = (e, targetId, items, date) => {
    e.stopPropagation();
    if (isMatchModalOpen) return;
    setMorePopoverState({
      isOpen: true, targetId: targetId, items: items,
      dateLabel: { day: date.toLocaleDateString('en-US', { weekday: 'short' }), num: date.getDate() },
      fullDate: date
    });
  };
  const closeMorePopover = () => { setMorePopoverState(prev => ({ ...prev, isOpen: false })); };

  const getCalendarDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (view === "month") {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
      for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    } else {
      const dayOfWeek = currentDate.getDay();
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - dayOfWeek);
      for (let i = 0; i < 7; i++) {
        const d = new Date(startOfWeek);
        d.setDate(startOfWeek.getDate() + i);
        days.push(d);
      }
    }
    return days;
  };

  const getMatchesForDay = (date) => {
    if (!date) return [];
    return matches.filter((item) => {
      const matchDate = new Date(item.match.start.timestamp * 1000);
      const isSameDay = matchDate.toDateString() === date.toDateString();
      if (!isSameDay) return false;
      if (showUpcomingOnly) {
        if (item.match.status === 'completed') return false;
      }
      return true;
    });
  };

  const getHeaderText = () => {
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    if (view === 'month') return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    const startMonth = monthNames[startOfWeek.getMonth()].substring(0, 3);
    const endMonth = monthNames[endOfWeek.getMonth()].substring(0, 3);
    if (startMonth === endMonth) return `${startMonth} ${startOfWeek.getFullYear()}`;
    return `${startMonth} - ${endMonth} ${endOfWeek.getFullYear()}`;
  };

  const handlePopoverEnter = () => {
    if (isMatchModalOpen) return;
    if (popoverCloseTimer.current) clearTimeout(popoverCloseTimer.current);
    setUpcomingPopoverOpen(true);
  };

  const handlePopoverLeave = () => {
    popoverCloseTimer.current = setTimeout(() => {
      setUpcomingPopoverOpen(false);
    }, 300);
  };

  const handleUpcomingCardClick = (tab) => {
    setUpcomingPopoverTab(tab);
    setUpcomingPopoverOpen(true);
  };

  const toggleSelectAll = () => {
    if (selectedUpcomingSlugs.size === allUpcomingMatches.length) {
      setSelectedUpcomingSlugs(new Set());
    } else {
      const allSlugs = new Set(allUpcomingMatches.map(m => m.match.m_slug));
      setSelectedUpcomingSlugs(allSlugs);
    }
  };

  const toggleMatchSelection = (slug) => {
    const newSet = new Set(selectedUpcomingSlugs);
    if (newSet.has(slug)) newSet.delete(slug);
    else newSet.add(slug);
    setSelectedUpcomingSlugs(newSet);
  };

  const renderModalContent = () => {
    if (!selectedMatch) return null;
    const isCompleted = selectedMatch.match.status === 'completed';
    const teams = selectedMatch.teams || [];
    const homeTeam = teams.find(t => t.position === 'home') || { name: 'Home' };
    const awayTeam = teams.find(t => t.position === 'away') || { name: 'Away' };
    const scoreHome = selectedMatch.score?.home ?? '-';
    const scoreAway = selectedMatch.score?.away ?? '-';
    let winnerName = "Draw";
    if (selectedMatch.match_result?.winner) {
      const winnerTeam = teams.find(t => t.key === selectedMatch.match_result.winner);
      if (winnerTeam) winnerName = winnerTeam.name;
    }

    if (isCompleted) {
      return (
        <div className="text-center py-3">
          <div className="score-board mb-3" style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold fs-5">{homeTeam.name}</span>
              <span className="badge badge-dark bg-dark fs-5 mx-2">{scoreHome} - {scoreAway}</span>
              <span className="fw-bold fs-5">{awayTeam.name}</span>
            </div>
            <div className="text-muted small">Final Score</div>
          </div>
          <div className="winner-section">
            <FontAwesomeIcon icon={faTrophy} className="text-warning mb-2" size="2x" />
            <h5 className="fw-bold text-success">{winnerName} Won</h5>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="d-flex justify-content-center mb-3">
          <Badge color={activeConfigs[selectedMatch.match.m_slug] ? "success" : "secondary"} className="p-2 fs-6">
            {activeConfigs[selectedMatch.match.m_slug] ? "LIVE BLOG ACTIVE" : "DISABLED"}
          </Badge>
        </div>
        <p className="text-center text-muted">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-danger" />
          {selectedMatch.match.stadium?.name}, {selectedMatch.match.stadium?.city}
        </p>
        <div className="d-grid gap-2">
          <Button
            color={activeConfigs[selectedMatch.match.m_slug] ? "danger" : "success"}
            onClick={(e) => handleToggle(selectedMatch, e)}
          >
            {activeConfigs[selectedMatch.match.m_slug] ? "Unsubscribe" : "Subscribe"}
          </Button>
        </div>
      </>
    );
  };

  const selectedTournamentName = filters.tslug
    ? (allTournaments.find(t => t.t_slug === filters.tslug)?.name || 'Selected Tournament')
    : (debouncedQuery ? `Search: "${debouncedQuery}"` : 'All Upcoming');

  const bulkSelectedMatches = allUpcomingMatches.filter(m => selectedUpcomingSlugs.has(m.match.m_slug));
  const isBulkAllActive = bulkSelectedMatches.length > 0 && bulkSelectedMatches.every(m => activeConfigs[m.match.m_slug]);
  const subscribedList = getSubscribedMatchList();

  // Date Change Handler for Picker
  const handleDateSelection = (newDate) => {
    setCurrentDate(newDate);
    setDatePickerOpen(false);
  };

 return (
  <div className="content gcal-wrapper">
    {/* High Z-Index for Toasts to sit on top of everything */}
    <ToastContainer position="top-right" autoClose={500} theme="colored" style={{zIndex: 99999}} />

    {/* --- HEADER LAYOUT WRAPPER --- */}
    <div className="header-layout-wrapper">

      {/* --- LEFT COLUMN --- */}
      <div className="header-main-column">

        {/* Row 1: Logo & Nav & Tools */}
        <div className="gcal-header-row">
          <div className="header-left">
            <div className="gcal-logo">⚽ <span className="d-none d-md-inline">Live Blog Manager</span></div>

            <div className="header-nav">
              <button className="today-btn-gcal" onClick={goToToday}>Today</button>

              <div className="nav-arrows">
                <button onClick={() => navigateDate(-1)}>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button onClick={() => navigateDate(1)}>
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>

              {/* SMART DATE SELECTOR TRIGGER */}
              <div 
                className="current-month-display"
                id="datePickerTarget"
                onClick={() => setDatePickerOpen(!datePickerOpen)}
              >
                <span className="month-text">{getHeaderText()}</span>
                <FontAwesomeIcon icon={faAngleDown} size="xs" className="dropdown-icon" />
              </div>
              
              <Popover
                placement="bottom-start"
                isOpen={datePickerOpen}
                target="datePickerTarget"
                toggle={() => setDatePickerOpen(!datePickerOpen)}
                trigger="legacy"
                className="shadow border-0"
                style={{zIndex: 2060}}
              >
                  <PopoverBody className="p-0">
                      <DateSelector 
                        currentDate={currentDate} 
                        onDateChange={handleDateSelection}
                      />
                  </PopoverBody>
              </Popover>

            </div>
          </div>

          <div className="header-tools-left">
            {/* Hide View Switcher on very small screens */}
            <div className="view-switcher me-2 d-none d-md-block">
              <div className="btn-group">
                <button
                  className={`view-btn ${view === 'month' ? 'active' : ''}`}
                  onClick={() => setView('month')}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                  Month
                </button>
                <button
                  className={`view-btn ${view === 'week' ? 'active' : ''}`}
                  onClick={() => setView('week')}
                >
                  <FontAwesomeIcon icon={faCalendarWeek} className="me-1" />
                  Week
                </button>
              </div>
            </div>

            <div className={`filter-expandable ${showSearch ? 'open' : ''}`}>
              <input
                type="text"
                placeholder="Search..."
                value={filters.query}
                onChange={(e) =>
                  handleFilterChange('query', e.target.value)
                }
              />
               {filters.query && (
                 <button
                   className="btn-clear-search"
                   onClick={() => handleFilterChange('query', '')}
                 >
                   <FontAwesomeIcon icon={faTimes} />
                 </button>
               )}
            </div>

            <button
              className={`icon-button ${showSearch ? 'active' : ''}`}
              onClick={() => setShowSearch(!showSearch)}
            >
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </div>
        </div>

        {/* Row 2: Filters Bar - Responsive logic */}
        <div className={`filters-bar-row ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="filter-item">
            <Select
              placeholder="Tournament"
              options={allTournaments.map(t => ({ label: t.name, value: t.t_slug }))}
              value={
                allTournaments
                  .map(t => ({ label: t.name, value: t.t_slug }))
                  .find(o => o.value === filters.tslug) || null
              }
              onChange={(selected) =>
                handleFilterChange('tslug', selected ? selected.value : '')
              }
              isClearable
              styles={{
                  control: (base) => ({ ...base, minHeight: '30px', height: '30px', fontSize: '12px' }),
                  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                  input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  indicatorsContainer: (base) => ({ ...base, height: '28px' })
              }}
            />
          </div>

          <div className="filter-item">
            <Select
              placeholder="Round"
              options={roundOptions.map(r => ({ label: r.name, value: r.r_slug }))}
              value={
                roundOptions
                  .map(r => ({ label: r.name, value: r.r_slug }))
                  .find(o => o.value === filters.roundSlug) || null
              }
              onChange={(selected) =>
                handleFilterChange('roundSlug', selected ? selected.value : '')
              }
              isClearable
              isDisabled={!filters.tslug}
              styles={{
                  control: (base) => ({ ...base, minHeight: '30px', height: '30px', fontSize: '12px' }),
                  valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                  input: (base) => ({ ...base, margin: 0, padding: 0 }),
                  indicatorsContainer: (base) => ({ ...base, height: '28px' })
              }}
            />
          </div>

          <div className="d-flex align-items-center ms-md-3 mt-2 mt-md-0" style={{ fontSize: '11px' }}>
            <input
              type="checkbox"
              className="form-check-input me-1"
              checked={showUpcomingOnly}
              onChange={(e) => setShowUpcomingOnly(e.target.checked)}
            />
            <label className="fw-bold text-secondary mb-0">
              Upcoming Only
            </label>
          </div>

          <button className="reset-btn-gcal ms-auto mt-2 mt-md-0" onClick={handleFilterReset}>
            Reset
          </button>
        </div>
      </div>

      {/* --- RIGHT COLUMN: RICH HEADER STATS --- */}
      <div className="header-stats-column">
        <div className="rich-stats-grid">
          
          {/* CARD 1: SUBSCRIPTIONS */}
          <div 
            className="rich-stat-card sub-card"
            id="subscribedBadgeTarget"
            onMouseEnter={handleSubscribedEnter}
            onMouseLeave={handleSubscribedLeave}
            onClick={() => setSubPopoverOpen(true)}
          >
            <div className="rich-icon-box icon-green">
               <FontAwesomeIcon icon={faBell} />
            </div>
            <div className="rich-text-col">
               <div className="rich-primary-row">
                  <span className="rich-num">{getActiveSubscribedCount()}</span>
                  <span className="rich-label">Active</span>
               </div>
               <div className="rich-sub-row text-success-muted">
                  <FontAwesomeIcon icon={faCheckCircle} size="xs" className="me-1"/>
                  {getCompletedSubscribedCount()} Done
               </div>
            </div>
          </div>

          {/* DIVIDER */}
          <div className="rich-divider d-none d-xl-block"></div>

          {/* CARD 2: DATABASE */}
          <div 
            className="rich-stat-card db-card"
            id="upcomingStatsTarget"
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverLeave}
            onClick={() => handleUpcomingCardClick('matches')}
          >
            <div className="rich-icon-box icon-blue">
               <FontAwesomeIcon icon={faCalendarAlt} />
            </div>
            <div className="rich-text-col">
               <div className="rich-primary-row">
                  <span className="rich-num">{allUpcomingMatches.length}</span>
                  <span className="rich-label">Upcoming</span>
               </div>
               <div className="rich-sub-row text-primary-muted">
                  <FontAwesomeIcon icon={faTrophy} size="xs" className="me-1"/>
                  {getUniqueUpcomingTournamentsCount()} Tours
               </div>
            </div>
          </div>

        </div>
      </div>
    </div>

      {/* POPOVERS */}
      {!isMatchModalOpen && (
        <Popover
          placement="bottom-end"
          isOpen={subPopoverOpen}
          target="subscribedBadgeTarget"
          toggle={() => { }}
          trigger="legacy"
          container="body"
          className="custom-popover-shadow super-priority-popover"
          style={{zIndex: 1060}}
        >
          <PopoverBody className="p-0" onMouseEnter={handleSubscribedEnter} onMouseLeave={handleSubscribedLeave}>
            <div className="popover-header-modern bg-success text-white border-0">
              <strong>Active Subscriptions {filters.tslug && "(Filtered)"}</strong>
            </div>
            <div className="popover-matches-list" style={{ maxHeight: '300px' }}>
              {subscribedList.length === 0 ? (
                <div className="p-3 text-center text-muted small">No active matches found.</div>
              ) : (
                subscribedList.map(m => {
                  const isCompleted = m.match.status === 'completed';
                  const isLive = m.match.status === 'started';
                  let rowClass = "popover-match-row";
                  if (isCompleted) rowClass += " row-is-completed";
                  else if (isLive) rowClass += " row-is-live";

                  return (
                    <div key={m.match.m_slug} className={rowClass}
                      onClick={() => { handleOpenMatchModal(m); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="match-time-col" style={{ width: '90px', color: '#333' }}>
                        {isCompleted ? <span className="status-finished">Ended</span> :
                          isLive ? <span className="status-live">LIVE</span> :
                            new Date(m.match.start.timestamp * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </div>
                      <div className="match-details-col">
                        <div className="match-teams">
                          {m.teams?.[0]?.code} <span className="vs">vs</span> {m.teams?.[1]?.code}
                        </div>
                      </div>
                      <div className="ms-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </PopoverBody>
        </Popover>
      )}

      {!isMatchModalOpen && (
        <Popover
          placement="bottom-end"
          isOpen={upcomingPopoverOpen}
          target="upcomingStatsTarget"
          toggle={() => { }}
          trigger="legacy"
          container="body"
          className="custom-popover-shadow super-priority-popover"
          style={{ maxWidth: '350px', zIndex: 1060 }}
        >
          <PopoverBody className="p-0" onMouseEnter={handlePopoverEnter} onMouseLeave={handlePopoverLeave}>
            
            {/* TABS HEADER */}
            <div className="popover-header-modern bg-light border-bottom p-0">
                <Nav tabs className="border-0">
                    <NavItem className="flex-fill text-center" style={{cursor: 'pointer'}}>
                        <NavLink 
                          className={classnames({ active: upcomingPopoverTab === 'matches' })}
                          onClick={() => setUpcomingPopoverTab('matches')}
                          style={{borderRadius: 0, padding: '10px 0', fontSize: '12px', fontWeight: 'bold'}}
                        >
                            Matches ({allUpcomingMatches.length})
                        </NavLink>
                    </NavItem>
                    <NavItem className="flex-fill text-center" style={{cursor: 'pointer'}}>
                        <NavLink 
                          className={classnames({ active: upcomingPopoverTab === 'tournaments' })}
                          onClick={() => setUpcomingPopoverTab('tournaments')}
                          style={{borderRadius: 0, padding: '10px 0', fontSize: '12px', fontWeight: 'bold'}}
                        >
                            Tournaments ({upcomingTournamentsList.length})
                        </NavLink>
                    </NavItem>
                </Nav>
            </div>

            {/* CONTENT: MATCHES LIST */}
            {upcomingPopoverTab === 'matches' && (
                <>
                <div className="p-2 bg-white border-bottom">
                  <div className="d-flex align-items-center bg-white p-2 border rounded shadow-sm">
                      <div className="form-check m-0 d-flex align-items-center">
                        <input
                            className="form-check-input mt-0"
                            type="checkbox"
                            id="selectAllMatches"
                            checked={selectedUpcomingSlugs.size === allUpcomingMatches.length && allUpcomingMatches.length > 0}
                            onChange={toggleSelectAll}
                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                        />
                        <label className="form-check-label ms-2 small fw-bold text-uppercase text-secondary mb-0" htmlFor="selectAllMatches" style={{ cursor: 'pointer', userSelect: 'none' }}>
                            Select All ({allUpcomingMatches.length})
                        </label>
                      </div>
                  </div>
                </div>

                <div className="popover-matches-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {loadingUpcoming ? (
                    <div className="p-3 text-center"><Spinner size="sm" color="secondary" /></div>
                  ) : allUpcomingMatches.length === 0 ? (
                    <div className="p-3 text-center text-muted"><small>No upcoming matches found.</small></div>
                  ) : (
                    allUpcomingMatches.map(m => {
                      const isSelected = selectedUpcomingSlugs.has(m.match.m_slug);
                      const isSubbed = activeConfigs[m.match.m_slug];
                      
                      let rowClass = "popover-match-row";
                      if (isSubbed) rowClass += " row-is-active"; 
                      
                      return (
                        <div
                          key={m.match.m_slug}
                          className={rowClass}
                          onClick={() => toggleMatchSelection(m.match.m_slug)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="me-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => { }}
                              style={{ pointerEvents: 'none' }}
                            />
                          </div>
                          <div className="match-time-col" style={{ width: '90px' }}>
                            {new Date(m.match.start.timestamp * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </div>
                          <div className="match-details-col">
                            <div className="match-teams">
                              {m.teams?.[0]?.code || 'Home'} <span className="vs">vs</span> {m.teams?.[1]?.code || 'Away'}
                            </div>
                          </div>
                          {isSubbed && <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2 small" />}
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="p-2 border-top bg-white">
                    <Button
                        color={isBulkAllActive ? "danger" : "success"}
                        size="sm"
                        className="w-100 btn-bulk-subscribe"
                        onClick={handleBatchToggle}
                        disabled={bulkLoading || selectedUpcomingSlugs.size === 0}
                    >
                        {bulkLoading ? <Spinner size="sm" /> : (
                        <FontAwesomeIcon icon={isBulkAllActive ? faTrash : faLayerGroup} className="me-1" />
                        )}
                        {isBulkAllActive
                        ? `Unsubscribe Selected (${selectedUpcomingSlugs.size})`
                        : `Subscribe Selected (${selectedUpcomingSlugs.size})`
                        }
                    </Button>
                </div>
                </>
            )}

            {/* CONTENT: TOURNAMENTS LIST */}
            {upcomingPopoverTab === 'tournaments' && (
                  <div className="popover-matches-list" style={{ maxHeight: '450px', overflowY: 'auto' }}>
                      {upcomingTournamentsList.length === 0 ? (
                          <div className="p-3 text-center text-muted"><small>No upcoming tournaments.</small></div>
                      ) : (
                        upcomingTournamentsList.map(group => {
                            const activeCount = group.matches.filter(m => activeConfigs[m.match.m_slug]).length;
                            const isAllActive = activeCount === group.matches.length;
                            
                            return (
                                <div key={group.tSlug} className="p-3 border-bottom hover-bg-light">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div className="fw-bold text-dark" style={{fontSize: '13px'}}>{group.info.name}</div>
                                        <Badge color="secondary" pill>{group.matches.length}</Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="text-muted small">
                                            {activeCount} Subscribed
                                        </div>
                                        <Button 
                                            size="sm" 
                                            color={isAllActive ? "outline-danger" : "outline-info"}
                                            style={{fontSize: '10px', padding: '2px 8px'}}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleGroupBatchToggle(group, !isAllActive);
                                            }}
                                        >
                                            {isAllActive ? "Unsubscribe All" : "Subscribe All"}
                                        </Button>
                                    </div>
                                </div>
                            );
                        })
                      )}
                  </div>
            )}

          </PopoverBody>
        </Popover>
      )}

      {/* --- CALENDAR GRID --- */}
      <div className="calendar-container" ref={calendarRef} onWheel={handleWheelScroll}>
        {(loading || findingDate) && (
          <div className="loading-overlay">
            <div className="d-flex flex-column align-items-center">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              {findingDate && <span className="mt-2 fw-bold text-dark" style={{ fontSize: '12px' }}>Locating match dates...</span>}
            </div>
          </div>
        )}

        <div className="days-header">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <div key={day} className="day-name">
                  <span className="d-none d-md-block">{day}</span>
                  <span className="d-block d-md-none">{day.charAt(0)}</span>
              </div>
          ))}
        </div>

        <div className={`days-grid ${view === 'week' ? 'week-view' : ''}`}>
          {getCalendarDays().map((date, index) => {
            if (!date) return <div key={index} className="day-cell empty"></div>;
            const isCurrentDay = (date.toDateString() === new Date().toDateString());
            const dayMatches = getMatchesForDay(date);
           const groupedMatches = dayMatches.reduce((groups, matchItem) => {
            const tSlug = matchItem.match.tournament.t_slug;
            if (!groups[tSlug]) {
                groups[tSlug] = { 
                    info: matchItem.match.tournament,
                    matches: [] 
                };
            }
            groups[tSlug].matches.push(matchItem);
            return groups;
            }, {});
            const groupsArray = Object.values(groupedMatches);
            
            // LOGIC FIX: On mobile, don't show any chips, show dots. If > 0 dots, row becomes clickable.
            const MAX_VISIBLE_ITEMS = view === 'week' ? 10 : (isMobileView ? 4 : 2);
            
            const visibleGroups = groupsArray.slice(0, MAX_VISIBLE_ITEMS);
            const hiddenCount = groupsArray.length - MAX_VISIBLE_ITEMS;
            const uniqueMoreId = `more-link-${date.getTime()}`;

            return (
              <div 
                key={index} 
                className={`day-cell ${isCurrentDay ? "today" : ""}`}
                // Mobile: Click cell to open details if events exist
                onClick={(e) => isMobileView && groupsArray.length > 0 && handleShowMoreClick(e, uniqueMoreId, groupsArray, date)}
              >
                <div className="day-number-row">
                  <span className={`day-number ${isCurrentDay ? 'active' : ''}`}>{date.getDate()}</span>
                  {view === 'week' && <span className="ms-2 text-muted small">{date.toLocaleDateString('en-US', { weekday: 'short' })}</span>}
                </div>
                <div className="events-container">
                  {visibleGroups.map((group, gIndex) => (
                    <TournamentGroup
                      key={`${date.getDate()}-${group.info.t_slug}-${gIndex}`}
                      group={group} date={date} activeConfigs={activeConfigs}
                      toggling={toggling} handleToggle={handleToggle}
                      setSelectedMatch={handleOpenMatchModal}
                      isMatchModalOpen={isMatchModalOpen}
                      handleGroupBatchToggle={handleGroupBatchToggle}
                      allMatches={matches}
                      isMobileView={isMobileView}
                    />
                  ))}
                  {hiddenCount > 0 && (
                    <div id={uniqueMoreId} className="more-events-link" onClick={(e) => handleShowMoreClick(e, uniqueMoreId, groupsArray, date)}>
                      {hiddenCount} more +
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {morePopoverState.isOpen && (
        <Popover
          placement="auto"
          isOpen={morePopoverState.isOpen}
          target={morePopoverState.targetId}
          toggle={closeMorePopover}
          className="more-events-popover"
          trigger="legacy"
          container="body"
          style={{ zIndex: 1070 }}
        >
          <PopoverBody className="p-0">
            <div className="more-popover-header">
              <div className="d-flex flex-column align-items-center">
                <span className="more-popover-day">{morePopoverState.dateLabel?.day}</span>
                <span className="more-popover-num">{morePopoverState.dateLabel?.num}</span>
              </div>
              <button className="btn-close-custom" onClick={closeMorePopover}><FontAwesomeIcon icon={faTimes} /></button>
            </div>
            <div className="more-popover-list">
              {morePopoverState.items.map((group, idx) => (
                <TournamentGroup
                  key={`more-${idx}`}
                  group={group}
                  date={morePopoverState.fullDate}
                  activeConfigs={activeConfigs}
                  toggling={toggling}
                  handleToggle={handleToggle}
                  setSelectedMatch={handleOpenMatchModal}
                  isMatchModalOpen={isMatchModalOpen}
                  handleGroupBatchToggle={handleGroupBatchToggle}
                  allMatches={matches}
                  // Force desktop view inside the popover even on mobile so users can see details
                  isMobileView={false} 
                />
              ))}
            </div>
          </PopoverBody>
        </Popover>
      )}

      {selectedMatch && (
        <Modal
          isOpen={!!selectedMatch}
          toggle={handleCloseMatchModal}
          centered
          modalClassName="match-modal-container"
        >
          <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)', color: 'white', borderBottom: 'none' }}>
            <div className="text-center w-100">
              <h5 className="modal-title fw-bold mb-1">{selectedMatch.match.title}</h5>
              <small className="opacity-75"><FontAwesomeIcon icon={faClock} className="me-1" />{new Date(selectedMatch.match.start.timestamp * 1000).toLocaleString('en-US')}</small>
            </div>
          </div>
          <ModalBody className="p-4">{renderModalContent()}</ModalBody>
        </Modal>
      )}

      <style>{`
        /* --- Z-INDEX PRIORITIES --- */
        
        /* 1. TOASTS (Highest Priority) */
        .Toastify__toast-container { z-index: 100000 !important; }

        /* 2. HEADER POPOVERS (Subscribed & Upcoming Lists) */
        /* Must sit above the Match Modal */
        .header-priority-popover { z-index: 10000 !important; }
        .header-priority-popover .popover { z-index: 10000 !important; }

        /* 3. MATCH MODAL */
        .match-modal-container { z-index: 5000 !important; }
        .modal-backdrop { z-index: 4999 !important; }

        /* 4. GRID POPOVERS (Tournament Chips / More Events) */
        /* These stay below the modal so if you click a match, the modal covers them */
        .custom-popover-shadow { z-index: 3000 !important; }
        
        /* --- HEADER LAYOUT & COMPACT MODE --- */
        .header-layout-wrapper { display: flex; flex-wrap: wrap; width: 100%; border-bottom: 1px solid #dadce0; background: #fff; position: relative; }
        .header-main-column { flex: 1 1 500px; display: flex; flex-direction: column; min-width: 0; }
        
        .gcal-header-row { 
            display: flex; 
            align-items: center; 
            justify-content: space-between; 
            padding: 2px 12px; /* COMPACT PADDING */
            min-height: 38px; /* REDUCED HEIGHT */
            flex-shrink: 0; 
            flex-wrap: wrap; 
            gap: 8px; 
            border-bottom: 1px solid #f1f3f4; 
        }

        .header-left { display: flex; align-items: center; gap: 10px; }
        .gcal-logo { display: flex; align-items: center; gap: 6px; font-size: 16px; color: #5f6368; white-space: nowrap; }
        .header-nav { display: flex; align-items: center; gap: 8px; }
        .today-btn-gcal { padding: 2px 10px; border: 1px solid #dadce0; border-radius: 4px; background: #fff; color: #3c4043; font-weight: 500; font-size: 11px; cursor: pointer; white-space: nowrap;}
        .today-btn-gcal:hover { background: #f1f3f4; }
        .nav-arrows { display: flex; align-items: center; }
        .nav-arrows button { background: none; border: none; cursor: pointer; color: #5f6368; padding: 4px; border-radius: 50%; font-size: 12px; margin: 0 1px; }
        .nav-arrows button:hover { background: #f1f3f4; color: #202124; }
        .current-month-display { font-size: 14px; color: #3c4043; min-width: 90px; white-space: nowrap; cursor: pointer; display: flex; align-items: center; gap: 4px;}
        .dropdown-icon { opacity: 0.5; }
        .header-tools-left { display: flex; align-items: center; gap: 4px; margin-left: auto; }
        
        /* DATE PICKER */
        .custom-date-picker { padding: 10px; width: 240px; }
        .picker-header { display: flex; justify-content: center; align-items: center; margin-bottom: 10px; font-size: 15px; font-weight: bold; }
        .picker-btn { cursor: pointer; padding: 2px 6px; border-radius: 4px; }
        .picker-btn:hover { background: #f1f3f4; }
        .picker-btn.active { color: #1a73e8; }
        .picker-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
        .picker-cell { text-align: center; padding: 6px 4px; cursor: pointer; border-radius: 4px; font-size: 12px; }
        .picker-cell:hover { background: #f1f3f4; }
        .picker-cell.selected { background: #1a73e8; color: white; }

        /* Filters Row - Compact */
        .filters-bar-row { 
            display: flex; 
            align-items: center; 
            padding: 2px 12px; 
            background: #f8f9fa; 
            border-bottom: 1px solid #e0e0e0; 
            min-height: 38px; /* REDUCED HEIGHT */
            flex-wrap: wrap;
            gap: 8px;
        }
        .filter-item { flex: 1; min-width: 130px; font-size: 11px; max-width: 180px; } 

        /* RICH HEADER STATS - Compact */
       .header-stats-column {
            flex: 0 0 auto;
            display: flex;
            align-items: center;
            padding: 0 16px;       /* Original padding */
            border-left: 1px solid #dadce0;
            background: #fff;
            min-height: 44px;      /* Original Height */
        }
        
        .rich-stats-grid { 
            display: flex; 
            align-items: center; 
            gap: 12px;             /* Original gap */
            height: 100%; 
            width: 100%; 
        }

        .rich-stat-card {
            display: flex;
            align-items: center;
            gap: 14px;
            padding: 8px 16px;
            cursor: pointer;
            border-radius: 12px;
            transition: background 0.2s;
            height: 38px;
            white-space: nowrap; /* Prevent text wrapping inside cards */
        }

        .rich-stat-card:hover {
            background-color: #f8f9fa;
        }

        .rich-icon-box {
            width: 42px;
            height: 42px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            flex-shrink: 0; /* Prevent icon from squashing */
        }        .icon-green { background-color: #e6f4ea; color: #137333; }
        .icon-blue { background-color: #e8f0fe; color: #1a73e8; }
        .rich-text-col { display: flex; flex-direction: column; justify-content: center; line-height: 1.1; }
        .rich-primary-row { display: flex; align-items: baseline; gap: 3px; }
        .rich-num { font-size: 13px; font-weight: 800; color: #202124; }
        .rich-label { font-size: 9px; font-weight: 700; text-transform: uppercase; color: #5f6368; }
        .rich-sub-row { font-size: 9px; font-weight: 600; margin-top: 1px; white-space: nowrap; }
        .text-success-muted { color: #1e8e3e; opacity: 0.9; }
        .text-primary-muted { color: #1967d2; opacity: 0.9; }
        .rich-divider { width: 1px; height: 0px; background-color: #dadce0; margin: 0 2px; }

        /* MOBILE MENU & RESPONSIVE LOGIC */
        .mobile-menu-btn { display: none; }
        
        @media (max-width: 1200px) {
             .header-main-column { border-right: none; border-bottom: 1px solid #f1f3f4; }
             .header-stats-column { border-left: none; justify-content: space-around; background-color: #fcfcfc; padding: 6px 12px; }
        }
        @media (max-width: 768px) {
             .mobile-menu-btn { display: flex; }
             .filters-bar-row { display: none; flex-direction: column; align-items: stretch; padding-bottom: 10px; height: auto; min-height: auto;}
             .filters-bar-row.mobile-open { display: flex; }
             .filter-item { max-width: 100%; }
             /* Hide text on mobile stats to save space */
             .rich-label { display: none; }
             /* IMPORTANT: Enable scrolling on mobile by removing fixed height */
             .gcal-wrapper { height: auto !important; min-height: 600px; overflow: visible !important; }
             .calendar-container { overflow: visible !important; }
             .days-grid { min-height: 500px; grid-template-rows: repeat(auto-fit, minmax(80px, 1fr)) !important; }
        }

        /* General Calendar Styles */
        .gcal-wrapper { position: relative; top: -10px !important; height: calc(100vh - 95px); display: flex; flex-direction: column; background: #ffffff; font-family: 'Google Sans', 'Roboto', Arial, sans-serif; overflow: hidden; border-radius: 8px; border: 1px solid #dadce0; padding-top: 0 !important; }
        
        .icon-button { background: none; border: none; cursor: pointer; width: 28px; height: 28px; border-radius: 50%; color: #5f6368; font-size: 14px; display: flex; align-items: center; justify-content: center; }
        .icon-button:hover { background: #f1f3f4; }
        .icon-button.active { background: #e8f0fe; color: #1a73e8; }
        .view-switcher .btn-group { border: 1px solid #dadce0; border-radius: 4px; overflow: hidden; display: flex; }
        .view-btn { border: none; background: #fff; color: #5f6368; padding: 3px 10px; font-size: 11px; font-weight: 500; cursor: pointer; transition: all 0.2s; border-right: 1px solid #dadce0; white-space: nowrap; }
        .view-btn:last-child { border-right: none; }
        .view-btn:hover { background: #f1f3f4; }
        .view-btn.active { background: #dc3545; color: white; }
        .filter-expandable { width: 0; overflow: hidden; transition: width 0.3s ease; position: relative;}
        .filter-expandable.open { width: 140px; }
        .filter-expandable input { width: 100%; padding: 2px 24px 2px 6px; border: 1px solid #dadce0; border-radius: 4px; background: #f1f3f4; border: none; font-size: 11px; }
        .reset-btn-gcal { background: none; border: none; color: #1a73e8; font-weight: 500; cursor: pointer; padding: 0; font-size: 11px; white-space: nowrap; }
        .btn-bulk-subscribe { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
        .btn-clear-search {position: absolute;right: 2px;top: 50%;transform: translateY(-50%);background: none;border: none;color: #5f6368;cursor: pointer;font-size: 10px;padding: 0;width: 20px;height: 100%;display: flex;align-items: center;justify-content: center;}
        .btn-clear-search:hover {color: #202124;}
        .calendar-container { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; }
        .loading-overlay { position: absolute; top:0; left:0; width:100%; height:100%; background: rgba(255,255,255,0.8); z-index: 50; display: flex; justify-content: center; align-items: center; }
        
        /* GRID FIXES */
        .days-header { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); border-bottom: 1px solid #dadce0; padding-top: 4px; padding-bottom: 2px; }
        .day-name { text-align: center; color: #70757a; font-size: 11px; font-weight: 800; text-transform: uppercase; padding-bottom: 4px; border-left: 1px solid transparent; }
        .day-name:first-child { border-left: 1px solid #dadce0; }
        .days-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); grid-template-rows: repeat(auto-fit, minmax(0, 1fr)); flex: 1; height: 100%; border-left: 1px solid #dadce0; }
        .days-grid.week-view { grid-template-rows: 1fr; }
        .day-cell { border-right: 1px solid #dadce0; border-bottom: 1px solid #dadce0; min-height: 0; display: flex; flex-direction: column; padding: 0px 2px; background: #fff; }
        .day-cell.empty { background: #f8f9fa; }
        .day-cell.today { background: #fff; } 
        .day-number-row { text-align: center; margin-bottom: 0px; padding-top: 2px; transition: margin 0.2s; }
        .day-number { display: inline-block; width: 20px; height: 20px; line-height: 20px; border-radius: 50%; font-size: 11px; font-weight: 700; color: #3c4043; transition: all 0.2s; }
        .day-cell.today .day-number.active { background: #1a73e8; color: #fff;  }
        .events-container { flex: 1; overflow: hidden; display: flex; flex-direction: column; gap: 1px; }
        .event-group-wrapper { width: 100%; }
        
        /* CHIPS */
        .event-chip { border-radius: 3px; padding: 0px 4px; font-size: 9px; cursor: pointer; display: flex; align-items: center; gap: 4px; width: 100%; transition: all 0.1s; border: 1px solid transparent; height: 16px; }
        .event-chip:hover { box-shadow: 0 1px 3px rgba(0,0,0,0.15); }
        .chip-upcoming { background: #fff; border-color: #4285f4; color: #1967d2; }
        .chip-live { background: #fce8e6; border-color: #ea4335; color: #c5221f; font-weight: 700; }
        .chip-active { background: #e6f4ea; border-color: #34a853; color: #137333; font-weight: 600; }
        .chip-active-completed { background: #e8eaed; border-color: #0d652d; color: #0d652d; opacity: 0.8; }
        .chip-completed { background: #f1f3f4; border-color: #dadce0; color: #5f6368; }
        .live-dot { height: 5px; width: 5px; background-color: #c5221f; border-radius: 50%; display: inline-block; animation: pulse 1.5s infinite; }
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }
        .count-badge { font-weight: 700; }
        .title-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }
        
        /* MOBILE DOT VIEW */
        .mobile-dot-view {
            padding: 0;
            justify-content: center;
            border: none !important;
            background: transparent !important;
            height: 8px;
        }

        .more-events-link { font-size: 10px; font-weight: 600; color: #3c4043; cursor: pointer; padding: 0px 4px; border-radius: 4px; margin-top: 1px; transition: background 0.2s; }
        .more-events-link:hover { background-color: #f1f3f4; color: #202124; }
        .more-events-popover { border: none; box-shadow: 0 8px 24px rgba(0,0,0,0.15); min-width: 250px; max-width: 300px; border-radius: 8px; }
        .more-popover-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 12px 16px 8px; }
        .more-popover-day { font-size: 11px; color: #70757a; font-weight: 600; text-transform: uppercase; line-height: 1; }
        .more-popover-num { font-size: 22px; color: #3c4043; line-height: 1; }
        .btn-close-custom { background: none; border: none; color: #5f6368; cursor: pointer; font-size: 16px; padding: 0; }
        .btn-close-custom:hover { color: #202124; }
        .more-popover-list { padding: 8px 12px 16px; display: flex; flex-direction: column; gap: 4px; max-height: 350px; overflow-y: auto; }
        .popover-header-modern { padding: 16px; background: #f8f9fa; border-bottom: 1px solid #eee; border-radius: 8px 8px 0 0; }
        .chip-live-header { background: #fce8e6; border-bottom-color: #fad2cf; }
        .chip-active-header { background: #e6f4ea; border-bottom-color: #ceead6; }
        .chip-active-completed-header { background: #e8eaed; border-bottom-color: #dadce0; }
        .popover-tournament-name { font-size: 14px; font-weight: 700; color: #202124; margin-bottom: 4px; }
        .popover-date-badge { font-size: 12px; color: #5f6368; display: flex; align-items: center; }
        .popover-matches-list { max-height: 300px; overflow-y: auto; padding: 4px 0; }
        .popover-match-row { display: flex; align-items: center; padding: 10px 16px; border-bottom: 1px solid #f1f3f4; transition: background 0.2s; border-left: 3px solid transparent; }
        .popover-match-row:last-child { border-bottom: none; }
        .popover-match-row:hover { background: #f8f9fa; }
        .row-is-live { border-left-color: #ea4335; background: #fff8f7; }
        .row-is-active { border-left-color: #34a853; background: #f6fbf7; }
        .row-was-active { border-left-color: #0d652d; background: #f1f8f3; opacity: 0.9; }
        .row-is-completed { border-left-color: #dadce0; opacity: 0.7; }
        .status-live { color: #c5221f; font-weight: 800; font-size: 10px; animation: pulse 1.5s infinite; }
        .match-time-col { width: 60px; font-size: 11px; color: #5f6368; font-weight: 500; }
        .status-finished { color: #5f6368; font-weight: 700; background: #e8eaed; padding: 2px 4px; border-radius: 4px; font-size: 10px; }
        .match-details-col { flex: 1; overflow: hidden; cursor: pointer; }
        .match-teams { font-size: 13px; font-weight: 600; color: #3c4043; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .match-teams .vs { font-weight: 400; color: #9aa0a6; font-size: 11px; margin: 0 4px; }
        .match-subtext { font-size: 11px; color: #70757a; margin-top: 2px; }
        .match-action-col { margin-left: 12px; }
        .action-btn { padding: 0; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 50%; }
        .action-btn:hover { background: #e8f0fe; }
        .icon-active { color: #34a853; }
        .icon-inactive { color: #dadce0; }
        .hover-bg-light:hover { background-color: #f8f9fa; }
      `}</style>
  </div>
 );
};

export default LiveBlogManager;